import React, { useCallback, useMemo } from "react"
import { addElement, getCountText, removeElementById } from "../../globalStates/utils"
import { EMPTY_COUPON, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { addValidatorsCoupon } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { newId } from "../../utils/Id"
import Page from "../../components/Page"
import CouponsSection from "./CouponsSection"
import { Coupons } from "../../globalStates/ExhibitorDataTypes"
import ButtonWithCaption from "../../components/ButtonWithCaption"

const CouponsPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const notificationTemplatesStates = useExhibitorDataState().getNotificationTemplates()
    const couponsLimit = serviceKeys.goodies || 10
    const couponsStates = useExhibitorDataState().getCoupons()
    const notificationCouponsStates = couponsStates.filter(
        (coupon) =>
            !(
                notificationTemplatesStates.filter((notification) => notification.action.get().param === coupon.id.get())
                    .length === 0
            )
    )

    const canAddCoupons = useMemo(() => {
        if (couponsStates.length - notificationCouponsStates.length >= couponsLimit) return false
        let valid = true
        couponsStates.forEach((couponsState) => {
            const name = couponsState.name.get()
            valid = valid && !!name && name.length >= 0
        })
        return valid
    }, [couponsLimit, couponsStates, notificationCouponsStates])

    const couponsDateStart = branding.configuration.couponsPage.sctCoupons.dateCouponsValidStart
    const couponsDateEnd = branding.configuration.couponsPage.sctCoupons.dateCouponsValidEnd

    const onAddCoupons = useCallback(() => {
        addElement<Coupons>(couponsStates, {
            ...EMPTY_COUPON,
            validStart: couponsDateStart ? new Date(couponsDateStart.toString()) : new Date(),
            validEnd: couponsDateEnd ? new Date(couponsDateEnd.toString()) : new Date(),
            id: newId().toString()
        })
        addValidatorsCoupon(couponsStates[couponsStates.length - 1])
    }, [couponsStates, couponsDateStart, couponsDateEnd])

    const onRemoveCoupons = useCallback(
        (productId: string) => {
            removeElementById<Coupons>(couponsStates, productId)
        },
        [couponsStates]
    )

    return (
        <Page description={branding.configuration.couponsPage.pageDescription}>
            {couponsStates?.map((couponsState, index) =>
                notificationCouponsStates.find((coupon) => coupon.id.get() === couponsState.id.get()) ? null : (
                    <CouponsSection
                        key={couponsState.id.get()}
                        couponsState={couponsState}
                        index={index}
                        onClickRemove={onRemoveCoupons}
                    />
                )
            )}

            <ButtonWithCaption
                id="addCoupons"
                caption={
                    branding.showCounter
                        ? getCountText(couponsStates.length - notificationCouponsStates.length, serviceKeys.goodies || 0)
                        : undefined
                }
                buttonText={branding.configuration.couponsPage.btnAddCoupons}
                onClick={onAddCoupons}
                disabled={!canAddCoupons}
            />
        </Page>
    )
}

export default CouponsPage
